import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PropertyLayout from './layout';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';
import UseAxios from '../../Api/http/http';
import { fetchCRZType } from '../../Api/request/propertyMasterApi';

const CONSTRUCTION_STAGES = [
  { id: 'In Progress', name: 'In Progress' },
  { id: 'Completed', name: 'Completed' },
];

const initialState = {
  constructionStage: '',
  carpetArea: '',
  builtUpSanctioned: '',
  builtUpActual: '',
  noOfFloors: '',
  noOfParking: '',
  noOfBedrooms: '',
  crzZone: '',
  furnishing: '',
  subType: '', // Add subType to state
};

function BuildingDetails({ prevStep, onNext, onSave, onBackToDashboard, initialValues, step = 3, totalSteps = 10, systemId, isView = false }) {
  const [form, setForm] = useState({ ...initialState, ...initialValues });
  const [errors] = useState({});
  const [CRZ_TYPES, setCRZTypes] = useState([]);

  // Use the API state hook
  const {
    isLoading,
    isSubmitting,
    error,
    submitError,
    clearErrors,
    fetchData,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save building details',
  });

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  // Clear builtUpActual if subType changes to not Flat/Shop
  useEffect(() => {
    if (form.subType && form.subType !== 'Flat' && form.subType !== 'Shop' && form.builtUpActual) {
      setForm((prev) => ({ ...prev, builtUpActual: '' }));
    }
  }, [form.subType]);

  // Fetch CRZ Types
  const fetchCRZTypes = async () => {
    try {
      const response = await fetchData(() => UseAxios(fetchCRZType()), {
        errorMessage: 'Failed to load CRZ types',
      });
      // Use the CRZ types directly from API response as they already have id and name
      setCRZTypes(response.data.crzTypes || []);
      console.log('CRZ Types response:', response);
    } catch (error) {
      console.error('Failed to fetch CRZ types:', error);
      // Fallback to default options if API fails
      setCRZTypes([
        { id: 1, name: 'Beach Side' },
        { id: 2, name: 'River Side' },
        { id: 3, name: 'Forest Side' },
      ]);
    }
  };

  useEffect(() => {
    fetchCRZTypes();
  }, []);

  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    const { name, value } = event.target;
    if (name === 'subType') {
      setForm((prev) => ({ ...prev, subType: value }));
      return;
    }
    // For decimal fields, parse as float
    if (['carpetArea', 'builtUpSanctioned', 'builtUpActual'].includes(name)) {
      setForm((prev) => ({ ...prev, [name]: value === '' ? '' : parseFloat(value) }));
    } else if (name === 'noOfFloors' || name === 'noOfParking') {
      setForm((prev) => ({ ...prev, [name]: value === '' ? '' : parseInt(value, 10) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDropdownChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, constructionStage: event.target.value.id }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();

    try {
      await submitData(async () => {
        // Only send builtUpActual for Flat/Shop
        const submitForm = {
          ...form,
          carpetArea: form.carpetArea === '' ? null : parseFloat(form.carpetArea),
          builtUpSanctioned: form.builtUpSanctioned === '' ? null : parseFloat(form.builtUpSanctioned),
          builtUpActual:
            form.subType === 'Flat' || form.subType === 'Shop'
              ? form.builtUpActual === '' ? null : parseFloat(form.builtUpActual)
              : undefined,
          noOfFloors: form.noOfFloors === '' ? null : parseInt(form.noOfFloors, 10),
          noOfParking: form.noOfParking === '' ? null : parseInt(form.noOfParking, 10),
          noOfBedrooms: form.noOfBedrooms === '' ? null : parseInt(form.noOfBedrooms, 10),
        };
        return Promise.resolve(submitForm);
      }, {
        successMessage: 'Building details saved successfully',
      });

      // Only send builtUpActual for Flat/Shop
      const submitForm = {
        ...form,
        carpetArea: form.carpetArea === '' ? null : parseFloat(form.carpetArea),
        builtUpSanctioned: form.builtUpSanctioned === '' ? null : parseFloat(form.builtUpSanctioned),
        builtUpActual:
          form.subType === 'Flat' || form.subType === 'Shop'
            ? form.builtUpActual === '' ? null : parseFloat(form.builtUpActual)
            : undefined,
        noOfFloors: form.noOfFloors === '' ? null : parseInt(form.noOfFloors, 10),
        noOfParking: form.noOfParking === '' ? null : parseInt(form.noOfParking, 10),
        noOfBedrooms: form.noOfBedrooms === '' ? null : parseInt(form.noOfBedrooms, 10),
      };
      onNext(submitForm);
    } catch (error) {
      console.error('Failed to save building details:', error);
    }
  };

  // Helper to save form data before navigating
  const handlePrev = () => {
    const submitForm = {
      ...form,
      carpetArea: form.carpetArea === '' ? null : parseFloat(form.carpetArea),
      builtUpSanctioned: form.builtUpSanctioned === '' ? null : parseFloat(form.builtUpSanctioned),
      builtUpActual: form.builtUpActual === '' ? null : parseFloat(form.builtUpActual),
      noOfFloors: form.noOfFloors === '' ? null : parseInt(form.noOfFloors, 10),
      noOfParking: form.noOfParking === '' ? null : parseInt(form.noOfParking, 10),
      noOfBedrooms: form.noOfBedrooms === '' ? null : parseInt(form.noOfBedrooms, 10),

    };
    onSave(submitForm); // Save form data only
    prevStep(); // Go to previous step
  };

  return (
    <>
      <LoaderOverlay isVisible={isLoading} message="Loading building details..." />

      <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
        <div className="card">
          <h3 className="card-title">
            <ApartmentIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Building Details
          </h3>

          {/* Display API errors */}
          {error && (
            <div className="error-message" style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              border: '1px solid #ffcdd2',
            }}>
              {error}
            </div>
          )}

          {submitError && (
            <div className="error-message" style={{
              padding: '10px',
              marginBottom: '15px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              borderRadius: '4px',
              border: '1px solid #ffcdd2',
            }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Row 1 */}
            <div className='form-row-three'>
              <div className="form-group">
                <SearchDropdown
                  label="Construction Stage *"
                  options={CONSTRUCTION_STAGES}
                  value={CONSTRUCTION_STAGES.find(opt => opt.id === form.constructionStage) || ''}
                  onChange={handleDropdownChange}
                  placeholder="Select construction stage"
                  dropdownCss="property-dropdown"
                  searchBox={false}
                  disabled={isSubmitting || isView}
                />
                {errors.constructionStage && <span className="error">{errors.constructionStage}</span>}
              </div>
            </div>

            {/* Row 2 */}
            <div className="form-row-three">
              <div className="form-group">
                <label>Carpet Area (Sq Ft)</label>
                <input
                  type="number"
                  name="carpetArea"
                  value={form.carpetArea}
                  onChange={handleChange}
                  placeholder="e.g., 1200"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className="form-group">
                <label>Built-up Area Sanctioned (Sq Ft)</label>
                <input
                  type="number"
                  name="builtUpSanctioned"
                  value={form.builtUpSanctioned}
                  onChange={handleChange}
                  placeholder="e.g., 1500"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              {/* Only show for NOT Flat/Shop */}
              {(form.subType !== 'Flat' && form.subType !== 'Shop') && (
                <div className="form-group">
                  <label>Built-up Area Actual (Sq Ft)</label>
                  <input
                    type="number"
                    name="builtUpActual"
                    value={form.builtUpActual}
                    onChange={handleChange}
                    placeholder="e.g., 1500"
                    disabled={isSubmitting || isView}
                    readOnly={isView}
                  />
                </div>
              )}
            </div>

            {/* Row 3: Floor Number, No Of Parking, No Of Bedrooms, CRZ Zone */}
            <div className="form-row-three">
              <div className="form-group">
                <label>Floor Number</label>
                <input
                  type="number"
                  name="noOfFloors"
                  value={form.noOfFloors}
                  onChange={handleChange}
                  placeholder="e.g., 2"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className="form-group">
                <label>No Of Bedrooms</label>
                <input
                  type="number"
                  name="noOfBedrooms"
                  value={form.noOfBedrooms}
                  onChange={handleChange}
                  placeholder="e.g., 3"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className="form-group">
                <label>Number of Parking</label>
                <input
                  type="number"
                  name="noOfParking"
                  value={form.noOfParking}
                  onChange={handleChange}
                  placeholder="e.g., 2"
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
            </div>

            {/* Row 4: CRZ Zone and Furnishing dropdowns in a single row */}
            <div className="form-row-three">
              <div className="form-group">
                <SearchDropdown
                  label="CRZ Zone"
                  options={CRZ_TYPES}
                  value={CRZ_TYPES.find(opt => opt.name === form.crzZone) || ''}
                  onChange={event => isView ? null : setForm(prev => ({ ...prev, crzZone: event.target.value.name }))}
                  placeholder="Select CRZ Zone"
                  dropdownCss="property-dropdown property-dropdown-lg"
                  searchBox={false}
                  disabled={isSubmitting || isLoading || isView}
                />
              </div>
              <div className="form-group">
                <SearchDropdown
                  label="Furnishing"
                  options={[{ id: 'None', name: 'None' }, { id: 'Furnished', name: 'Furnished' }, { id: 'Semi-furnished', name: 'Semi-furnished' }]}
                  value={[{ id: 'None', name: 'None' }, { id: 'Furnished', name: 'Furnished' }, { id: 'Semi-furnished', name: 'Semi-furnished' }].find(opt => opt.id === form.furnishing) || ''}
                  onChange={event => isView ? null : setForm(prev => ({ ...prev, furnishing: event.target.value.id }))}
                  placeholder="Select Furnishing"
                  dropdownCss="property-dropdown property-dropdown-lg"
                  searchBox={false}
                  disabled={isSubmitting || isView}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="form-actions">
              <button
                type="button"
                className="btn secondary"
                onClick={handlePrev}
                disabled={isSubmitting}
              >
                Previous
              </button>
              {isView ? (
                <button
                  type='button'
                  className='btn primary'
                  onClick={() => onNext(form)}
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Next'}
                </button>
              )}
            </div>
          </form>
        </div>
      </PropertyLayout>
    </>
  );
}

BuildingDetails.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isView: PropTypes.bool,
};

export default BuildingDetails;
